/*
 * SEE IT IN YOUR ROOM — the storefront client.
 *
 * One press, one upload, one image back. Every decision that could differ
 * between page loads is read from the block's `data-vis` at CLICK TIME, never
 * cached at load time: Horizon refetches and replaces the product-information
 * section on every variant change, so a config captured at boot would describe
 * the shape the customer first saw rather than the one they chose.
 *
 * THE PHOTO IS DOWNSCALED HERE, AND THAT IS A SERVER DECISION MADE ON THE
 * CLIENT. A phone photograph is 3-12 MB; the model tiles its input to 768 px
 * regardless, so the pixels above ~1280 px are paid for in upload time, in
 * Worker CPU to re-encode, and in nothing else. Downscaling in a canvas costs
 * the customer about 40 ms and removes a whole class of failure — oversized
 * bodies, base64 CPU on the edge, slow mobile uploads — before it can happen.
 * It is also what makes the EXIF stripping below free: a canvas re-encode
 * carries no metadata, so no GPS coordinate ever leaves the device.
 *
 * THE PROGRESS IS REAL. It counts the seconds that have actually elapsed and
 * says what it is waiting for. It does NOT run a scripted six-step narration on
 * a timer — the competitor does, and a bar that advances while nothing is
 * happening is the false-comfort pattern this project has removed twice.
 *
 * IT NEVER RETRIES BY ITSELF. A render is a paid call, and an automatic retry
 * on a slow answer is how one press becomes three charges. A failure asks the
 * customer to press again.
 */
(function () {
  'use strict';

  var MAX_EDGE = 1280;        /* long edge sent to the Worker */
  var JPEG_Q = 0.85;
  var IDLE_MS = 45000;        /* silence, not duration — see readNdjson below  */
  /* WHAT A RENDER ACTUALLY TAKES, MEASURED, IN ONE PLACE. The wait is the
   * feature's worst property and it is not fixable by wording, so the wording's
   * only job is to be TRUE: it says roughly how long before the customer
   * commits, counts real seconds while they wait, and says plainly when this
   * one has gone past normal instead of leaving them to wonder.
   *
   * RE-DERIVE THIS FROM src/11_visualise/bench/latency.py IF THE REQUEST
   * CHANGES — image size, output resolution or model all move it, and a number
   * that used to be true is worse than no number. */
  var TYPICAL_S = 20;         /* measured median, gemini-3.1-flash-image @2K   */
  var SLOW_AFTER_S = 50;      /* past this, say so rather than keep counting   */
  var SID_KEY = 'rugzella_vis_sid';

  /* ── small helpers ───────────────────────────────────────────────────── */
  function $(sel, root) { return (root || document).querySelector(sel); }

  function sid() {
    try {
      var v = localStorage.getItem(SID_KEY);
      if (!v) {
        v = 'v-' + Date.now().toString(36) + '-' +
            Math.random().toString(36).slice(2, 10);
        localStorage.setItem(SID_KEY, v);
      }
      return v;
    } catch (e) {
      /* Private mode, or storage blocked. A session id is a rate-limit key,
       * not an identity, so an ephemeral one is correct rather than fatal. */
      return 'v-nostore-' + Date.now().toString(36);
    }
  }

  function cfgOf(box) {
    try { return JSON.parse(box.getAttribute('data-vis') || '{}'); }
    catch (e) { return {}; }
  }

  /* ── reading the photo ───────────────────────────────────────────────── */
  /* ONE PLACE DECIDES WHAT THE MODEL IS SENT, and both entry points go through
   * it. The rug finder already holds a full-resolution canvas of the same room
   * photo, but it encodes at 1024/q0.82 for the ANALYSER — a different model
   * with different needs. Handing that encode to the visualiser would silently
   * ship a lower-quality render from the finder than from a product page, and
   * nothing would report the difference. It hands over the CANVAS instead and
   * this function decides. */
  function encodeCanvas(canvas, w, h) {
    var s = Math.min(1, MAX_EDGE / Math.max(w, h));
    var cw = Math.max(1, Math.round(w * s));
    var chh = Math.max(1, Math.round(h * s));
    var c = document.createElement('canvas');
    c.width = cw; c.height = chh;
    var ctx = c.getContext('2d');
    /* White under the draw: a transparent source would otherwise composite onto
     * black and the model would be handed a photograph of a room at midnight. */
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, chh);
    ctx.drawImage(canvas, 0, 0, cw, chh);
    var durl = c.toDataURL('image/jpeg', JPEG_Q);
    return { data: durl.slice(durl.indexOf(',') + 1), w: cw, h: chh };
  }

  /* Returns { data, w, h } — base64 JPEG with no prefix, plus the dimensions
   * the Worker needs to pick an output aspect ratio near the customer's own. */
  function readPhoto(file) {
    return new Promise(function (resolve, reject) {
      if (!/^image\//.test(file.type)) {
        reject(new Error('not_an_image')); return;
      }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h) { reject(new Error('unreadable')); return; }
          resolve(encodeCanvas(img, w, h));
        } catch (e) { reject(new Error('decode_failed')); }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url); reject(new Error('unreadable'));
      };
      img.src = url;
    });
  }

  /* ── status: a progress bar whose every pixel is an elapsed second ──────
   * WHAT PROGRESS DATA ACTUALLY EXISTS, MEASURED IN THE WORKER RATHER THAN
   * ASSUMED: the stream carries `{t:'start'}`, `{t:'tick', s:<seconds>}` every
   * two seconds, and exactly one `{t:'done'}`. There is NO fraction and no
   * percentage anywhere, because the model call is a single opaque await that
   * returns the whole image at once - Gemini reports nothing in between. The
   * upload is not the number worth showing either: the photo encode measures
   * 19ms and everything except the model accounts for ~1.3s of a 74s render.
   *
   * SO THE BAR IS DRIVEN BY THE ONE NUMBER THAT IS TRUE - seconds actually
   * elapsed - against the MEASURED median, and it is shaped so it cannot lie:
   *
   *     fill = CEILING * (1 - exp(-t / TYPICAL_S))
   *
   * It moves quickly through the common case, slows as it goes, and ASYMPTOTES
   * BELOW FULL. It reaches the end only when the image is actually in hand. A
   * bar that reads 47% would be inventing a number; this one is a rendering of
   * the clock, and the label says the clock out loud beside it.
   *
   * `aria-valuenow` IS DELIBERATELY NOT SET. A screen reader announces it as a
   * fraction of the work completed, which is exactly the claim this cannot
   * make. `aria-valuetext` carries the sentence instead.
   *
   * ── IT READS AS A PERCENTAGE NOW, NOT AS SECONDS (2026-09-05) ───────────
   * By direct decision of the user. The number displayed is the SAME curve the
   * fill already draws - nothing new is derived and nothing new is claimed -
   * printed the way a person expects to read a progress bar. Two rules make it
   * honest and both are enforced here rather than promised in a comment:
   *
   *   1. IT NEVER REACHES 100 UNTIL THE IMAGE IS IN HAND. The curve asymptotes
   *      at BAR_CEILING, and `Math.floor` on the way to the label means even
   *      the ceiling prints as 92 rather than rounding up to 93. Only
   *      `finish()` - which only a real image can call - writes 100.
   *   2. IT NEVER GOES BACKWARDS. The curve is monotonic in elapsed seconds,
   *      but the CLOCK is not: a laptop waking, an NTP correction or a system
   *      clock change can hand `Date.now()` an earlier value, and a bar that
   *      slides from 60% to 40% reads as work being undone. `lastPct` latches
   *      it, so the worst a clock jump can do is stall the number.
   *
   * The elapsed seconds have not gone away - they moved into `aria-valuetext`,
   * where the honest sentence has always lived. */
  var BAR_CEILING = 0.93;

  function Status(box) {
    var elStatus = $('[data-vis-status]', box);
    var timer = null, t0 = 0, lastPct = 0;
    var fill = null, lead = null, clock = null, wrap = null;

    function build(msg, withBar) {
      elStatus.hidden = false;
      elStatus.className = 'rug-vis__status' + (withBar ? '' : ' rug-vis__status--bad');
      elStatus.textContent = '';
      if (!withBar) {
        elStatus.appendChild(document.createTextNode(msg));
        fill = lead = clock = wrap = null;
        return;
      }
      wrap = document.createElement('div');
      wrap.className = 'rug-vis__prog';
      wrap.setAttribute('role', 'progressbar');
      wrap.setAttribute('aria-valuetext', msg);

      var track = document.createElement('div');
      track.className = 'rug-vis__track';
      fill = document.createElement('div');
      fill.className = 'rug-vis__fill';
      track.appendChild(fill);

      var text = document.createElement('div');
      text.className = 'rug-vis__progtext';
      lead = document.createElement('span');
      clock = document.createElement('b');
      text.appendChild(lead);
      text.appendChild(clock);

      wrap.appendChild(track);
      wrap.appendChild(text);
      elStatus.appendChild(wrap);
      lead.textContent = msg;
      clock.textContent = '0%';
    }

    function paintBar(msg, s) {
      if (!fill) return;
      var pct = Math.max(lastPct, BAR_CEILING * (1 - Math.exp(-s / TYPICAL_S)));
      lastPct = pct;
      fill.style.inlineSize = (pct * 100).toFixed(1) + '%';
      fill.style.width = (pct * 100).toFixed(1) + '%';
      lead.textContent = msg;
      clock.textContent = Math.floor(pct * 100) + '%';
      wrap.setAttribute('aria-valuetext', msg + ', ' + s + ' seconds so far');
    }

    return {
      working: function (msg, opts) {
        var slow = !!(opts && opts.slow);
        t0 = Date.now();
        lastPct = 0;
        build(msg, true);
        clearInterval(timer);
        paintBar(msg, 0);
        timer = setInterval(function () {
          var s = Math.round((Date.now() - t0) / 1000);
          /* PAST NORMAL IS A FACT, AND SAYING IT IS THE HONEST THING. A bar
           * that keeps creeping past the typical time with the same sentence
           * beside it reads as a hang; naming it reads as a slow render, which
           * is what it is. */
          paintBar(slow && s >= SLOW_AFTER_S
            ? 'Still working - this one is taking longer than usual'
            : msg, s);
        }, 1000);
      },
      /* THE BAR ONLY EVER REACHES THE END ON A REAL RESULT. */
      finish: function () {
        clearInterval(timer); timer = null;
        lastPct = 1;
        if (fill) {
          fill.style.inlineSize = '100%';
          fill.style.width = '100%';
          if (clock) clock.textContent = '100%';
          if (wrap) wrap.setAttribute('aria-valuetext', 'Done');
          if (wrap) wrap.className = 'rug-vis__prog rug-vis__prog--done';
        }
      },
      fail: function (msg) { clearInterval(timer); timer = null; build(msg, false); },
      clear: function () { clearInterval(timer); timer = null; elStatus.hidden = true; }
    };
  }

  /* ── the streamed answer ─────────────────────────────────────────────── */
  /* The Worker writes one JSON object per line: a `start`, a `tick` every two
   * seconds while the model is working, then exactly one `done`. `kick` resets
   * the caller's inactivity watchdog on every line received, which is what
   * makes a 90-second render survive iOS's 60-second request timeout — that
   * timeout is per-inactivity, not per-request, so a byte every two seconds
   * defeats it.
   *
   * A STREAM THAT ENDS WITHOUT A `done` IS A FAILURE, NOT AN EMPTY SUCCESS.
   * The connection dropping mid-render and the render finishing with nothing to
   * show are different events and the customer can act on neither if they look
   * the same, so the terminal line is required rather than assumed. */
  function readNdjson(res, kick, onRoom) {
    if (!res.body || !res.body.getReader) {
      /* No streaming support. The whole body arrives at once, which is exactly
       * what this design was avoiding — but by then it has already arrived, so
       * parse it rather than failing. */
      return res.text().then(function (t) { return lastDone(t); });
    }
    var reader = res.body.getReader();
    var dec = new TextDecoder();
    var buf = '';
    var out = null;

    function pump() {
      return reader.read().then(function (chunk) {
        if (chunk.done) {
          return out || { ok: false, reason: 'network' };
        }
        kick();
        buf += dec.decode(chunk.value, { stream: true });
        var nl;
        while ((nl = buf.indexOf('\n')) !== -1) {
          var raw = buf.slice(0, nl); buf = buf.slice(nl + 1);
          if (!raw.trim()) continue;
          var obj;
          try { obj = JSON.parse(raw); } catch (e) { continue; }
          /* THE ROOM READ ARRIVES ON THE FIRST LINE AND IS ACTED ON THERE.
           * The gate answers before the model is even called, so waiting for
           * `done` would hold the size back behind a 20-70 second render it
           * does not depend on - and would lose it entirely on a render that
           * failed, which is exactly when a customer still needs the size. */
          if (obj.t === 'start' && obj.room && onRoom) { try { onRoom(obj.room); } catch (e) {} }
          if (obj.t === 'done') out = obj;
        }
        if (out) { try { reader.cancel(); } catch (e) {} return out; }
        return pump();
      });
    }
    return pump();
  }

  function lastDone(text) {
    var lines = String(text || '').split('\n');
    for (var i = lines.length - 1; i >= 0; i--) {
      if (!lines[i].trim()) continue;
      try {
        var o = JSON.parse(lines[i]);
        if (o.t === 'done') return o;
      } catch (e) { /* not a line we wrote */ }
    }
    return { ok: false, reason: 'network' };
  }

  /* A FAILURE MUST SAY WHOSE IT IS. "Our software is broken", "you are over a
   * limit" and "that photograph is not a room" are three different things and
   * a customer can only act on the last two. */
  var MESSAGES = {
    not_an_image: 'That file is not an image. Try a photo from your camera roll.',
    unreadable: 'We could not open that photo. Try a different one.',
    decode_failed: 'We could not read that photo on this device. Try a different one.',
    too_large: 'That photo is too large to send. Try one from your camera roll.',
    origin: 'This preview is not available on this page.',
    rate: 'You have made a few of these recently. Try again in an hour.',
    session: 'You have used your previews for today. They reset tomorrow.',
    cap: 'We have hit today’s limit for this preview. Please try again tomorrow.',
    rug_unavailable: 'We could not load this rug’s photo just now. Please try again in a moment.',
    bad_request: 'We could not use that photo. Try a different one.',
    server: 'Something went wrong on our side. Please try again.',
    busy: 'The image service is busy right now. Please try again in a few minutes.',
    timeout: 'That took longer than expected and we stopped waiting. Please try again.',
    not_a_room: 'We could not find a floor in that photo. Try a wider shot of the room.',
    declined: 'We could not use that photo. Try a different one.',
    network: 'We could not reach the preview service. Check your connection and try again.',
    /* SCRIPT NEVER LOADED — the one case where a content blocker really is the
     * likely cause, so it is the only case that says so. */
    challenge_unavailable: 'The security check could not load. If you use a '
      + 'content blocker, allow challenges.cloudflare.com on this page and try again.',
    /* OUR CONFIGURATION, NOT THEIRS. 110200 is "domain not authorized"; the
     * 1101xx and 400020 family are a bad or unknown site key. Telling a
     * customer to check their ad blocker for any of these is a code fault
     * dressed up as a product verdict, which is exactly what CLAUDE.md §12
     * forbids — and it is what shipped. */
    challenge_110200: 'This preview is not set up correctly for this page yet. '
      + 'It is our configuration, not your browser — we have been told.',
    challenge_110100: 'This preview is not set up correctly for this page yet. '
      + 'It is our configuration, not your browser — we have been told.',
    challenge_110110: 'This preview is not set up correctly for this page yet. '
      + 'It is our configuration, not your browser — we have been told.',
    challenge_400020: 'This preview is not set up correctly for this page yet. '
      + 'It is our configuration, not your browser — we have been told.',
    challenge_timeout: 'The security check did not finish in time. Please try again.',
    challenge_expired: 'The security check expired. Please try again.'
  };
  function messageFor(reason) {
    if (MESSAGES[reason]) return MESSAGES[reason];
    /* A challenge code we have not enumerated is a REAL challenge failure —
     * Cloudflare's 300* and 600* families mean it decided this looked like a
     * bot. That is not our fault and not something an ad blocker explains, so
     * it gets its own honest sentence rather than the generic one. */
    if (/^challenge_/.test(String(reason || ''))) {
      return 'The security check could not be completed. Please try again.';
    }
    return 'Something went wrong on our side. Please try again.';
  }

  /* ── the challenge ───────────────────────────────────────────────────── */
  /* THE SCRIPT IS LOADED FROM HERE, LAZILY, NOT FROM LIQUID. Horizon re-renders
   * the product-information section on every variant change, and re-inserting a
   * <script> element re-executes it — so a tag in the block would re-run
   * Turnstile's loader every time the customer picked a different size. Loading
   * it on first use is idempotent by construction, and it also means the
   * external request only happens for someone who actually pressed the button
   * rather than on every product page view.
   *
   * A FAILED LOAD IS CACHED AS A FAILURE FOR ONE ATTEMPT ONLY. Caching a
   * rejected promise forever would turn one bad network moment into a dead
   * feature for the rest of the visit. */
  var TS_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var TS_LOAD_MS = 12000;
  var tsLoading = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (tsLoading) return tsLoading;
    tsLoading = new Promise(function (resolve, reject) {
      var done = false;
      var fail = function () {
        if (done) return;
        done = true; tsLoading = null;
        reject(new Error('challenge_unavailable'));
      };
      var s = document.createElement('script');
      s.src = TS_SRC; s.async = true; s.defer = true;
      s.onload = function () {
        if (done) return;
        if (window.turnstile) { done = true; resolve(window.turnstile); } else fail();
      };
      s.onerror = fail;
      setTimeout(fail, TS_LOAD_MS);
      document.head.appendChild(s);
    });
    return tsLoading;
  }

  /* Resolves to a fresh token, or to null when no sitekey is configured.
   *
   * `execution: 'execute'` and `appearance: 'interaction-only'` together mean
   * nothing is shown and nothing runs until we ask for it — so the widget costs
   * a customer no space and no interaction unless Cloudflare decides this
   * visitor needs one. A token is single-use and expires in 300 s, so the
   * widget is RESET before every execute rather than reused; a stale token
   * would be refused by the Worker and read to the customer as a rate limit. */
  function challengeToken(box, cfg) {
    if (!cfg.sitekey) return Promise.resolve(null);
    return loadTurnstile().then(function (ts) {
      return new Promise(function (resolve, reject) {
        var host = $('[data-vis-turnstile]', box);
        if (!host) { reject(new Error('challenge_unavailable')); return; }
        var settle = function (fn, v) {
          if (!host._vsPending) return;
          host._vsPending = null;
          clearTimeout(host._vsTimer);
          fn(v);
        };
        host._vsPending = true;
        host._vsResolve = function (t) { settle(resolve, t); };
        /* THE CODE IS CARRIED, NOT DISCARDED. Turnstile's error-callback hands
         * back a numeric code that says exactly what went wrong, and the first
         * version of this threw it away and printed a guess about content
         * blockers. It cost a live outage that read as the customer's fault:
         * the real code was 110200, "domain not authorized", which is OUR
         * configuration and nothing a customer can do anything about. */
        host._vsReject = function (code) {
          var c = String(code == null ? '' : code);
          if (window.console && console.warn) {
            console.warn('[rug-visualiser] Turnstile error ' + (c || '(no code)') +
              ' on ' + location.hostname);
          }
          settle(reject, new Error('challenge_' + (c || 'unavailable')));
        };
        host._vsTimer = setTimeout(function () { host._vsReject('timeout'); }, TS_LOAD_MS);

        try {
          if (host._vsId == null) {
            host._vsId = ts.render(host, {
              sitekey: cfg.sitekey,
              execution: 'execute',
              appearance: 'interaction-only',
              /* Read through the element rather than closed over, so the
               * handlers registered on the FIRST render still settle the
               * CURRENT attempt. */
              callback: function (t) { if (host._vsResolve) host._vsResolve(t); },
              'error-callback': function (c) { if (host._vsReject) host._vsReject(c); },
              'timeout-callback': function () { if (host._vsReject) host._vsReject('timeout'); },
              'expired-callback': function () { if (host._vsReject) host._vsReject('expired'); }
            });
          } else {
            ts.reset(host._vsId);
          }
          ts.execute(host._vsId);
        } catch (e) { host._vsReject('threw'); }
      });
    });
  }

  /* ── degrading instead of disappearing ───────────────────────────────── */
  /* AT THE CAP THE PAGE MUST STILL ANSWER THE QUESTION. A feature that vanishes
   * on the best traffic day of the year is worse than one that says what it
   * cannot do and offers the thing that actually settles the decision — which,
   * for "will this rug work in my room", is not a picture at all.
   *
   * The wording is lifted from the rug finder's own tape card deliberately: one
   * voice, and it is already the most honest sentence on the site. */
  var CAPPED_KEY = 'rugzella_vis_capped';

  function rememberCapped(reason) {
    try { sessionStorage.setItem(CAPPED_KEY, reason); } catch (e) { /* private mode */ }
  }
  function cappedReason() {
    try { return sessionStorage.getItem(CAPPED_KEY); } catch (e) { return null; }
  }

  var CAP_HEADING = {
    cap: 'The room preview has reached today’s limit',
    session: 'You have used your room previews for today',
    rate: 'A few too many previews from this connection just now'
  };
  var CAP_LEAD = {
    cap: 'It resets tomorrow. In the meantime, here is the check that actually '
       + 'settles it:',
    session: 'They reset tomorrow. In the meantime, here is the check that '
       + 'actually settles it:',
    rate: 'Try again in an hour. In the meantime, here is the check that '
       + 'actually settles it:'
  };

  /* THE SIZE AS THE CUSTOMER WAS SHOWN IT, IN INCHES. The product page passes
   * the full Shopify option string — `2'7" x 3'3"  (80x100 cm)` — and the rug
   * finder passes the imperial half of the same string. Both are trimmed to the
   * imperial part here, because the store sells in the US and a masking-tape
   * instruction in centimetres is one a customer cannot follow.
   *
   * UNIT-AGNOSTIC ON PURPOSE: it returns the label rather than re-deriving a
   * measurement, so it cannot disagree with the size the customer actually
   * picked. Anything with no digits in it is not a size and abstains. */
  function sizeLabel(s) {
    var t = String(s || '');
    var i = t.indexOf('(');
    t = (i > 0 ? t.slice(0, i) : t).replace(/\s+/g, ' ').trim();
    return /\d/.test(t) ? t : null;
  }

  function degrade(box, cfg, reason) {
    var host = $('[data-vis-fallback]', box);
    if (!host) return false;
    host.textContent = '';
    host.appendChild(el('h3', 'rug-vis__fbtitle',
      CAP_HEADING[reason] || CAP_HEADING.cap));
    host.appendChild(el('p', 'rug-vis__fblead', CAP_LEAD[reason] || CAP_LEAD.cap));

    var dims = sizeLabel(cfg.size);
    host.appendChild(el('p', 'rug-vis__fbtape',
      'Mark ' + (dims || 'the size you are considering') + ' out on your floor '
      + 'with masking tape and stand where you would sit. It is what IKEA tells '
      + 'its own customers to do, and it is more reliable than any picture — '
      + 'including ours.'));

    var a = el('a', 'rug-vis__fblink',
      'Or let us work out the size from a photo of your room');
    a.href = '/pages/rug-finder';
    host.appendChild(a);

    host.hidden = false;
    return true;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
  }


  /* ══ THE SIZE ANSWER ════════════════════════════════════════════════════
   * THE PICTURE IS NOT THE POINT OF THE POPUP ANY MORE - THE SIZE IS. By
   * direct decision of the user (2026-09-05): "After the render, one thing
   * matters: the size they should buy... A person should learn 'buy 200x300'
   * in one glance, without reading a paragraph."
   *
   * WHERE THE ANSWER COMES FROM, IN ORDER, AND NOT ONE STEP OF IT IS INVENTED
   * HERE:
   *   1. THE ROOM READ. The visualiser's free gate was widened to return the
   *      room type and one furniture count alongside `is_room` - the same
   *      call, the same photograph, no second request and no extra latency.
   *      The alternative was a second paid call to the room analyser per press.
   *   2. THE BAND. `FitEngine.bandFor` looks the (room, placement, input) cell
   *      up in the published fit index. Those cells are computed by
   *      `placement.py` at BUILD time, so this is the RUG FINDER'S OWN
   *      calculation rather than a second one - which is what the brief asked
   *      for in as many words.
   *   3. THE SIZE. Filtered from THIS PAGE'S OWN `<option>` list, so a size we
   *      name is a size this product is actually sold in, and its variant id
   *      comes from the same element rather than from a lookup that could
   *      resolve to a different rug.
   *
   * AND WHEN THE ROOM CANNOT BE READ IT SAYS SO AND SENDS THEM TO THE RUG
   * FINDER. The gate FAILS OPEN, so a broken checker renders the picture with
   * no reading at all; printing a band derived from a room nobody looked at
   * would be exactly the invented recommendation this whole feature exists to
   * replace. `read: false` and "the room is a living room" are opposite claims
   * and they are never allowed to look the same. */

  var IDX = null, idxWaiting = null;

  /* Fetched when the MODAL OPENS, not when the page loads and not when the
   * render returns. The page must not pay for it unless somebody presses the
   * button; and by the time a 20-70 second render comes back it has long since
   * arrived, so it is never on the critical path. */
  function loadIndex(pointer) {
    if (IDX) return Promise.resolve(IDX);
    if (idxWaiting) return idxWaiting;
    if (!pointer || !/^https?:\/\//i.test(pointer)) return Promise.resolve(null);
    var bust = 'v=' + Math.floor(Date.now() / 60000);
    idxWaiting = fetch(pointer + (pointer.indexOf('?') < 0 ? '?' : '&') + bust)
      .then(function (r) { if (!r.ok) throw new Error('pointer'); return r.json(); })
      .then(function (ptr) {
        var base = pointer.replace(/\/fit\/current\.json.*$/, '/');
        return fetch(base + ptr.index).then(function (r) {
          if (!r.ok) throw new Error('index'); return r.json();
        });
      })
      .then(function (d) { IDX = d; return d; })
      .catch(function () { idxWaiting = null; return null; });
    return idxWaiting;
  }

  /* THE PAGE'S OWN SIZE CONTROL. `options[Size]` is what Horizon names it; the
   * class is the fallback for a picker rendered without the name. */
  function sizeSelect() {
    return document.querySelector('select[name="options[Size]"]')
        || document.querySelector('.variant-picker select');
  }

  function pageSizes() {
    var sel = sizeSelect();
    if (!sel) return [];
    var out = [];
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      var vid = o.getAttribute('data-variant-id') || '';
      if (!vid) continue;              /* a size this shape is not made in */
      out.push({ option: o.value, variantId: vid,
                 sold_out: /unavailable/i.test(o.textContent || '') });
    }
    return out;
  }

  var ROOM_WORD = { living: 'living room', bedroom: 'bedroom',
                    dining: 'dining room', hallway: 'hallway',
                    kitchen: 'kitchen', office: 'home office' };

  /* Everything the popup needs to name one size, or an honest reason it
   * cannot. Returns null when there is nothing to say at all. */
  function recommendation(idx, roomRead) {
    if (!idx || !roomRead || !roomRead.read || !roomRead.room) return null;
    var room = roomRead.room;
    var placements = (idx.placements || {})[room] || [];
    var placement = placements[0];
    if (!placement) return null;
    var key = window.FitEngine.inputKeyFor(room, roomRead);
    var band = window.FitEngine.bandFor(idx, room, placement, key);
    if (!band || !band.ok) return { room: room, band: band, sizes: [] };
    var pool = pageSizes();
    var hits = window.FitEngine.sizesInBand(pool.map(function (s) { return s.option; }), band);
    var byOption = {};
    pool.forEach(function (s) { byOption[s.option] = s; });
    var sizes = hits.map(function (h) {
      var meta = byOption[h.option] || {};
      return { w: h.w, l: h.l, option: h.option,
               variantId: meta.variantId, sold_out: !!meta.sold_out };
    });
    /* A SOLD-OUT SIZE IS NOT THE ANSWER TO "WHAT SHOULD I BUY". It stays in
     * the list, because the band is a fact about the room and hiding a size
     * the room wants would misstate it, but the one put forward is the largest
     * that can actually be bought. */
    var buyable = sizes.filter(function (s) { return !s.sold_out; });
    return { room: room, band: band, sizes: sizes,
             pick: buyable[0] || sizes[0] || null };
  }

  /* SELECTING IT ON THE PAGE BEHIND IS THE WHOLE POINT OF THE CONTROL. The
   * customer will not remember "200x300" thirty seconds after the popup
   * closes, so the popup sets the variant rather than telling them to. It goes
   * through the page's own `<select>` and its own `change` event - Horizon
   * updates the price, the URL, the buy button and everything else that
   * listens - rather than reaching past it into the cart form, which would
   * leave the page displaying one size and the form carrying another. */
  function selectOnPage(option) {
    var sel = sizeSelect();
    if (!sel) return false;
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === option) {
        if (sel.selectedIndex === i) return true;
        sel.selectedIndex = i;
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  /* ── ADDING IS `assets/rug-cart.js` NOW (2026-09-06) ────────────────────
   * This function used to POST the line itself and then dispatch
   * `new CustomEvent('shopify:cart:lines-update', { detail: { source: … } })`.
   * The NAME was right and the SHAPE was not: every listener in this theme
   * reads `event.promise`, a CustomEvent has none, and `?.` swallowed it -
   * so the line landed and the header bubble stayed at 0 and stayed hidden.
   * MEASURED: server cart 7 -> 8 in 435 ms, bubble still reading "0".
   *
   * That is what "adding to cart takes too long" was. The POST itself is
   * 363-771 ms, which is Shopify's own write and the same round trip the
   * page's own Add to cart pays; the missing half was any sign it had
   * happened. `rug-cart.js` carries the full account, and the same inert
   * dispatch existed in `rug-finder.js`, which is why the fix is one shared
   * file rather than two corrections.
   *
   * IF THE HELPER IS NOT ON THE PAGE THE POPUP STILL SELLS. The block loads it
   * before this file, so the fallback should be unreachable - but a customer
   * who cannot add a rug because a notification helper is missing is a far
   * worse failure than a stale cart icon. */
  function addToCart(variantId) {
    if (window.RugCart && window.RugCart.add) {
      return window.RugCart.add(variantId, 1, 'rug-visualiser');
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  /* THE IMPERIAL LABEL IS THE CATALOGUE'S OWN STRING, taken from before the
   * bracket. Nothing is converted, so the number in the popup, the number in
   * the picker and the number on the cart line are one string. */
  function bigLabel(option) {
    var i = String(option).indexOf('(');
    return i > 0 ? String(option).slice(0, i).trim() : String(option);
  }

  function metricLabel(option) {
    var m = /\((\d+\s*[xX]\s*\d+\s*cm)\)/.exec(String(option));
    return m ? m[1].replace(/\s+/g, ' ') : '';
  }

  function renderSize(box, cfg, roomRead) {
    var wrap = $('[data-vis-size]', box);
    var none = $('[data-vis-size-none]', box);
    if (!wrap || !none) return;
    wrap.hidden = true; none.hidden = true;

    loadIndex(cfg.pointer).then(function (idx) {
      var rec = recommendation(idx, roomRead);
      if (!rec || !rec.pick) {
        /* FOUR DIFFERENT ANSWERS, AND ONLY ONE OF THEM IS ABOUT THE ROOM.
         * Collapsing them was measured to say the wrong thing: a Crea rug sold
         * in seven rectangle sizes, none of which reaches a queen bedroom,
         * reported "no size WE MAKE fits a room like this" - a claim about the
         * whole catalogue produced by one product's short range, and false,
         * because the Rug Finder answers it in one click. */
        var why = $('[data-vis-size-why]', none);
        if (why) {
          var msg;
          if (!idx) {
            msg = 'We could not load the size guide just now.';
          } else if (!roomRead || !roomRead.read) {
            msg = 'We could not read the room well enough to name a size.';
          } else if (rec && rec.band && rec.band.ok === false && rec.band.reason) {
            /* The band's OWN refusal - a real answer about the room, such as a
             * layout that would need a rug wider than the loom can weave. */
            msg = rec.band.reason;
          } else if (rec && rec.band && rec.band.ok) {
            msg = 'This rug is not made in a size that suits a ' +
              (ROOM_WORD[rec.room] || 'room') + ' like this — it needs about ' +
              rec.band.along[0] + '-' + rec.band.along[1] + ' by ' +
              rec.band.across[0] + '-' + rec.band.across[1] + ' cm.';
          } else {
            msg = 'We could not work out a size for a room like this one.';
          }
          why.textContent = msg;
        }
        none.hidden = false;
        return;
      }
      var pick = rec.pick;
      var lead = $('[data-vis-size-lead]', wrap);
      var big = $('[data-vis-size-big]', wrap);
      var sub = $('[data-vis-size-sub]', wrap);
      var btn = $('[data-vis-size-add]', wrap);
      var note = $('[data-vis-size-note]', wrap);

      if (lead) lead.textContent = 'For a ' + (ROOM_WORD[rec.room] || 'room')
        + ' like this, buy';
      if (big) big.textContent = bigLabel(pick.option);
      if (sub) sub.textContent = metricLabel(pick.option);
      if (note) note.textContent = rec.band.rule || '';

      if (btn) {
        btn.disabled = false;
        btn.textContent = pick.sold_out
          ? 'This size is sold out'
          : (cfg.addLabel || 'Add this size to my cart');
        btn.disabled = !!pick.sold_out;
        btn.onclick = function () {
          selectOnPage(pick.option);
          if (pick.sold_out || !pick.variantId) return;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          addToCart(pick.variantId).then(function () {
            btn.textContent = 'Added — it is in your cart';
            /* ── THE POPUP CLOSES SO THE DRAWER IS NOT BEHIND A MODAL ──────
             * (2026-09-06, by direct decision.) The cart drawer auto-opens on
             * `action: 'add'`, and its own deferral - wait for the source
             * modal's `close` before opening - CANNOT engage here: `rug-cart`
             * dispatches on `document`, so `event.target` is not an Element
             * inside this dialog and `closest('dialog:modal')` finds nothing.
             * Left alone, the drawer opens OVER a live modal and the customer
             * has two stacked layers to dismiss.
             *
             * IT CLOSES ONLY ON A RESOLVED ADD. A failed one keeps the popup
             * open, because the retry button is in it and closing would hide
             * the one control that can recover.
             *
             * The close is deferred a frame so the button's own label paints
             * first - the press has to be seen to have worked even by someone
             * watching the popup rather than the header. */
            var box = btn.closest && btn.closest('dialog[data-vis-modal]');
            if (box) requestAnimationFrame(function () { closeModal(box); });
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = 'That did not go through — try the button on the page';
          });
        };
      }
      /* THE PAGE BEHIND FOLLOWS THE ANSWER IMMEDIATELY, not on the click.
       * "Closing the popup leaves the right size chosen" is the brief, and a
       * customer who reads the size and closes without pressing anything is
       * the common case rather than the exception. */
      selectOnPage(pick.option);
      wrap.hidden = false;
    });
  }

  /* ── the run ─────────────────────────────────────────────────────────── */
  var inFlight = false;

  function run(box, source) {
    if (inFlight) return;
    var cfg = cfgOf(box);
    if (!cfg.endpoint || !cfg.rug) return;

    var status = Status(box);
    var result = $('[data-vis-result]', box);
    var img = $('[data-vis-img]', box);
    var caption = $('[data-vis-caption]', box);
    var bar = $('[data-vis-pick]', box);
    var label = $('[data-vis-label]', box);
    var fallback = $('[data-vis-fallback]', box);

    /* ALREADY KNOWN TO BE AT THE CAP — do not upload, do not spend a round
     * trip, and do not make the customer wait to be told the same thing twice.
     * The first refusal of the visit costs one ~1s POST; every one after it is
     * answered from the page. */
    var known = cappedReason();
    if (known && degrade(box, cfg, known)) {
      status.clear();
      result.hidden = true;
      return;
    }

    inFlight = true;
    /* THE SHARED SHELL READS THIS, NOT `inFlight` - it is in another
     * closure. Mirrored rather than exported so the flag stays owned
     * here and the shell stays ignorant of what 'busy' means. */
    try { window.RugModal.setBusy($('[data-vis-modal]', box), true); } catch (e) {}
    if (bar) bar.disabled = true;
    result.hidden = true;
    if (fallback) fallback.hidden = true;
    status.working('Reading your photo');

    var ctl = null, timeoutId = null;
    try { ctl = new AbortController(); } catch (e) { ctl = null; }

    var first = source && source.canvas
      ? Promise.resolve(encodeCanvas(source.canvas, source.w, source.h))
      : readPhoto(source);

    first.then(function (p) {
      /* THE CHALLENGE RUNS AFTER THE FILE IS CHOSEN, NOT BEFORE. If Cloudflare
       * decides this visitor needs an interactive step, it should appear once
       * they have committed to the action — not as a gate on a page they are
       * still browsing. */
      status.working('Checking');
      return challengeToken(box, cfg).then(function (token) {
        return { p: p, token: token };
      });
    }).then(function (both) {
      var p = both.p;
      status.working('Placing the rug in your room — usually about '
        + TYPICAL_S + ' seconds', { slow: true });
      var body = JSON.stringify({
        image: p.data, w: p.w, h: p.h,
        rug: cfg.rug,
        shape: cfg.shape || '',
        size: cfg.size || '',
        sku: cfg.sku || '',
        handle: cfg.handle || '',
        sid: sid(),
        /* Named so a slow render is attributable to an entry point without a
         * browser session. The finder sets it in its own config. */
        surface: cfg.surface || 'product',
        token: both.token || undefined
      });
      if (body.length > 9000000) return Promise.reject(new Error('too_large'));

      var opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body };
      if (ctl) {
        opts.signal = ctl.signal;
        /* AN INACTIVITY WATCHDOG, NOT A TOTAL DEADLINE. The Worker heartbeats
         * every two seconds for exactly as long as the model takes, so silence
         * — not duration — is what means something has gone wrong. A total
         * deadline would cut off a render that was working perfectly well and
         * had already been paid for. */
        timeoutId = setTimeout(function () { try { ctl.abort(); } catch (e) {} }, IDLE_MS);
      }
      return fetch(cfg.endpoint, opts);
    }).then(function (res) {
      var kick = function () {
        if (!ctl) return;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(function () { try { ctl.abort(); } catch (e) {} }, IDLE_MS);
      };
      /* Everything that fails BEFORE the model call answers with an ordinary
       * status code and a JSON body — origin, validation, the caps. Only the
       * render itself is streamed, so the content type is what says which
       * shape came back. */
      var ct = (res.headers.get('content-type') || '').toLowerCase();
      if (ct.indexOf('ndjson') === -1) {
        return res.json().then(function (d) { return { d: d }; },
          function () { return { d: { ok: false, reason: 'server' } }; });
      }
      return readNdjson(res, kick, function (roomRead) {
        renderSize(box, cfg, roomRead);
      }).then(function (d) { return { d: d }; });
    }).then(function (r) {
      if (timeoutId) clearTimeout(timeoutId);
      var d = r.d || {};
      if (!d.ok || !d.image) {
        /* THREE OF THESE ARE NOT ERRORS, THEY ARE LIMITS — and a limit deserves
         * an answer rather than an apology. The budget being spent says nothing
         * about the customer's photograph and nothing about our software; what
         * it means is that this particular picture is unavailable and the
         * question behind it still needs settling. */
        if (d.reason === 'cap' || d.reason === 'session' || d.reason === 'rate') {
          rememberCapped(d.reason);
          if (degrade(box, cfg, d.reason)) { status.clear(); return; }
        }
        status.fail(messageFor(d.reason));
        return;
      }
      /* THE BAR REACHES THE END HERE AND NOWHERE ELSE, which is what makes it
       * a rendering of the clock rather than a claim about the model: the only
       * thing that can complete it is an image in hand. */
      status.finish();
      img.src = 'data:' + (d.mime || 'image/jpeg') + ';base64,' + d.image;
      /* THE CAPTION ONLY APPEARS WHEN IT HAS SOMETHING LOAD-BEARING TO SAY.
       * It used to restate the rug and the size under every render, which is
       * the paragraph the brief asked to be cut - the customer is looking at
       * the picture and the size is now the biggest thing on the panel. What
       * it must still say, when it is true, is that the pattern shown comes
       * from another SHAPE of this rug, or that the render came back at
       * reduced resolution. Those are not decoration: they are the two ways
       * the picture is not quite what it appears to be. */
      var cap = captionFor(cfg, d);
      caption.textContent = cap;
      caption.hidden = !cap;
      /* A `done` without a `start` cannot happen on a streamed answer, but a
       * non-streamed one (no ReadableStream support) reaches here with the
       * room read only on this line. Idempotent: rendering it twice paints the
       * same answer. */
      if (d.room) renderSize(box, cfg, d.room);
      result.hidden = false;
      try { result.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
      catch (e) { /* ancient browser */ }
    }).catch(function (e) {
      if (timeoutId) clearTimeout(timeoutId);
      var name = (e && e.name) || '';
      var msg = (e && e.message) || '';
      status.fail(messageFor(
        name === 'AbortError' ? 'timeout' :
        MESSAGES[msg] ? msg : 'network'));
    }).then(function () {
      inFlight = false;
      try { window.RugModal.setBusy($('[data-vis-modal]', box), false); } catch (e) {}
      if (bar) bar.disabled = false;
      /* After any completed attempt the button's job is the same — send
       * another photo — so it says so rather than reverting to "Add a room
       * photo", which reads as though nothing happened. */
      if (label) label.textContent = 'Try another photo';
      if (bar) bar.removeAttribute('disabled');
    });
  }

  /* THE CAPTION IS A CAVEAT LINE NOW, NOT A RESTATEMENT (2026-09-05). It used
   * to name the rug and the size under every render; the brief cut that -
   * "after the render, one thing matters: the size they should buy" - and the
   * size is now the largest thing on the panel, so repeating it in small grey
   * text underneath was the paragraph in the way of it.
   *
   * WHAT MUST SURVIVE IS THE TWO WAYS THE PICTURE IS NOT QUITE WHAT IT LOOKS
   * LIKE: a pattern borrowed from another SHAPE of this rug, which would
   * otherwise be presented as this variant's own photograph, and a render that
   * came back at reduced resolution. Neither is decoration and neither is
   * inferable from looking. When both are false the element renders nothing at
   * all rather than an empty line. */
  function captionFor(cfg, d) {
    var bits = [];
    if (cfg.shapeIsOwn === false) {
      bits.push('Pattern shown from another shape of the same rug.');
    }
    if (d && d.fallback_size) bits.push('Rendered at reduced resolution.');
    return bits.join('  ');
  }

  /* ── binding, once, on the document ──────────────────────────────────── */
  /* DELEGATED SO IT SURVIVES A SECTION SWAP. Horizon replaces the whole
   * product-information section on every variant change; a listener bound to
   * the button at load time would be attached to a detached node afterwards,
   * and the bar would silently stop working the first time somebody picked a
   * different size. */
  /* THE ONE ENTRY POINT FOR ANYTHING THAT IS NOT THE PRODUCT-PAGE BAR.
   * The rug finder calls this with the room photo the customer already gave it,
   * so nobody is asked for the same picture twice. Everything downstream — the
   * challenge, the caps, the budget object, the degraded panel — is shared by
   * construction rather than by two implementations agreeing. */
  window.RugVisualiser = {
    open: function (box, source) {
      if (!box || !source) return;
      run(box, source);
    },
    busy: function () { return inFlight; }
  };

  /* ── the modal ───────────────────────────────────────────────────────────
   * THE OPENER IS NOT INSIDE THE BOX ANY MORE. The badge lives over the
   * product images and the config lives in the buy column, so `closest()`
   * cannot find one from the other. There is exactly ONE `[data-rug-vis]` on a
   * product page - one config, one modal - so falling back to the page's only
   * one is a lookup rather than a guess, and it is what lets the badge sit
   * wherever it reads best without the payload being computed twice. */
  function boxFor(node) {
    return (node && node.closest && node.closest('[data-rug-vis]'))
      || document.querySelector('[data-rug-vis]');
  }

  /* ── THE MODAL SHELL, SHARED (2026-09-07) ─────────────────────────────────
   * `window.RugModal` is the ONE implementation of "open a dialog, close it
   * three ways" on this store. It was this file's private `openModal` /
   * `closeModal` plus a delegated click handler; the rug finder needed the
   * same behaviour for its details popup and the instruction was explicit -
   * "reuse its shell rather than writing a second modal".
   *
   * WHAT IS SHARED IS THE MECHANISM, AND THE POLICY STAYS WITH ITS OWNER.
   * `data-rz-modal-busy` is how a caller says "not now": this file sets it
   * while a paid render is in flight, because closing then throws away a call
   * that has already been billed. The finder sets it never. One handler, two
   * policies, and neither has to know about the other.
   *
   * IT LIVES HERE RATHER THAN IN rug-finder.js because this file is loaded on
   * BOTH surfaces - the finder calls `window.RugVisualiser.open` - and the
   * product page does not load the finder. The dependency already runs that
   * way; reversing it would put a modal the product page needs inside a file
   * it does not have.
   *
   * ESCAPE IS THE BROWSER'S. `<dialog>` fires `cancel`, which is where a busy
   * dialog is protected without re-implementing key handling. */
  var RugModal = {
    open: function (dlg) {
      if (!dlg) return false;
      if (typeof dlg.showModal === 'function') {
        if (!dlg.open) dlg.showModal();
      } else {
        /* No <dialog> support: it still renders, it is still reachable, and it
         * loses only the backdrop and the focus trap. A feature that degrades
         * is better than one that throws. */
        dlg.setAttribute('open', '');
      }
      return true;
    },
    close: function (dlg) {
      if (!dlg) return;
      if (dlg.hasAttribute && dlg.hasAttribute('data-rz-modal-busy')) return;
      if (typeof dlg.close === 'function' && dlg.open) dlg.close();
      else dlg.removeAttribute('open');
    },
    /* `busy` is set by the OWNER of the dialog, not by this module, because
     * only the owner knows what it is in the middle of. */
    setBusy: function (dlg, on) {
      if (!dlg) return;
      if (on) dlg.setAttribute('data-rz-modal-busy', '');
      else dlg.removeAttribute('data-rz-modal-busy');
    }
  };
  window.RugModal = RugModal;

  /* THE THREE CLOSE ROUTES, ONCE, FOR EVERY `[data-rz-modal]` ON THE PAGE. */
  document.addEventListener('click', function (e) {
    var node = e.target;
    if (!node || !node.closest) return;
    var x = node.closest('[data-rz-modal-close]');
    if (x) {
      e.preventDefault();
      RugModal.close(x.closest('dialog'));
      return;
    }
    /* CLICK OUTSIDE. `<dialog>` gives Escape and the backdrop, but not this:
     * the backdrop is a pseudo-element and receives no events, so a click on it
     * is delivered to the DIALOG - confirmed with `elementFromPoint` at a point
     * far outside the box, which returns the dialog. The test is "not inside
     * the panel" rather than "the target IS the dialog", because the panel
     * fills the dialog exactly and hit-testing at the shared edge returned the
     * dialog for a point that is visually inside the panel. */
    var dlg = node.closest('dialog[data-rz-modal]');
    if (dlg && !node.closest('[data-rz-modal-panel]')) RugModal.close(dlg);
  });

  document.addEventListener('cancel', function (e) {
    var dlg = e.target;
    if (!dlg || !dlg.matches || !dlg.matches('dialog[data-rz-modal]')) return;
    if (dlg.hasAttribute('data-rz-modal-busy')) e.preventDefault();
  }, true);

  function openModal(box) {
    var dlg = $('[data-vis-modal]', box);
    return dlg ? RugModal.open(dlg) : false;
  }

  function closeModal(dlg) { RugModal.close(dlg); }

  document.addEventListener('click', function (e) {
    var node = e.target;
    if (!node || !node.closest) return;

    /* THE BADGE, wherever it is. */
    var opener = node.closest('[data-vis-open]');
    if (opener) {
      var box = boxFor(opener);
      if (box && openModal(box)) e.preventDefault();
      return;
    }

    /* THE PLUS FRAME -> the file input. */
    var pick = node.closest('[data-vis-pick]');
    if (pick) {
      e.preventDefault();
      var pbox = boxFor(pick);
      var input = pbox && $('[data-vis-file]', pbox);
      if (input) input.click();
      return;
    }

    /* THE X, ESCAPE AND CLICK-OUTSIDE ARE THE SHARED SHELL'S NOW - see
     * `RugModal` above. This handler keeps only what is specific to the
     * visualiser: the badge that OPENS it, and the plus frame.
     *
     * ONE BEHAVIOUR CHANGED AND IT IS DELIBERATE. The X used to close even
     * mid-render while click-outside did not; both now respect
     * `data-rz-modal-busy`, so nothing can throw away a call that has already
     * been billed. Closing was never the way to stop a render - the client's
     * own watchdog is - and a customer who presses X mid-render and loses the
     * picture they paid for is the worse of the two. */
  });

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('[data-vis-file]')) return;
    var box = input.closest('[data-rug-vis]');
    var file = input.files && input.files[0];
    /* Cleared so choosing the SAME file twice fires `change` again — otherwise
     * a customer who retries after a failure gets no event at all. */
    input.value = '';
    if (box && file) run(box, file);
  });
}());
